import { Component, Host, Prop, h, Watch, Element } from '@stencil/core';
import type {
  ResponsiveImages,
  HotSpot,
  CssClassMap,
} from '../../helpers/utils/interfaces';
import { isEnterKey, renderResponsiveImages } from '../../helpers/helpers';
import type { CmsOverlay } from '../cms-overlay/cms-overlay';

@Component({
  tag: 'cms-infographic',
  styleUrl: 'cms-infographic.scss',
  shadow: true,
})
export class CmsInfographic {
  private responsiveImages: ResponsiveImages;
  private hotSpots: Array<HotSpot>;
  private overlay: CmsOverlay;

  @Element() hostElement: HTMLElement;

  /**
   * Set  Layout full 100% or two-thirds 66%
   */
  @Prop({ mutable: true }) layout: 'full' | 'two-thirds';

  /**
   * set the responsive images for Breakpoints 320, 480, 768,992,1200
   */
  @Prop({ mutable: true }) images: ResponsiveImages | string;

  /**
   * set the HotSpot image map
   */
  @Prop({ mutable: true }) propHotSpots: Array<HotSpot> | string;

  /**
   * set the open Overlay Text
   */
  @Prop({ mutable: true }) openOverlayText: string;

  /**
   * set the overlay state
   */
  @Prop({ mutable: true }) open = false;

  /**
   * set the overlay headline
   */
  @Prop({ mutable: true }) overlayHeadline: string;

  /**
   * set the image alt text
   */
  @Prop({ mutable: true }) imageAltText = 'Hornbach';

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  @Watch('images')
  private imagesWatcher(newValue: string | ResponsiveImages) {
    if (newValue) {
      this.responsiveImages =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  @Watch('propHotSpots')
  private hotSpotsWatcher(newValue: string | HotSpot[]) {
    if (newValue) {
      this.hotSpots =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  componentDidLoad() {
    this.overlay = this.hostElement.shadowRoot.querySelector(
      'cms-overlay',
    ) as unknown as CmsOverlay;
  }

  private responsiveImagesSizes() {
    if (this.layout === 'full') {
      return `
      (max-width: 1279px) and (min-resolution: 288dpi) 33vw,
      (max-width: 1279px) and (min-resolution: 192dpi) 50vw,
      (max-width: 1279px) and (min-resolution: 96dpi) 100vw,
      (min-width: 1280px) and (min-resolution: 288dpi) 768px,
      (min-width: 1280px) and (min-resolution: 192dpi) 768px,
      (min-width: 1280px) and (min-resolution: 96dpi) 1280px,
      100vw`;
    }

    if (this.layout === 'two-thirds') {
      return `
      (max-width: 767px) and (min-resolution: 288dpi) 33vw,
      (max-width: 767px) and (min-resolution: 192dpi) 50vw,
      (max-width: 767px) and (min-resolution: 96dpi) 100vw,
      (min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 33vw,
      (min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 33vw,
      (min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 66vw,
      (min-width: 1280px) and (min-resolution: 288dpi) 400px,
      (min-width: 1280px) and (min-resolution: 192dpi) 400px,
      (min-width: 1280px) and (min-resolution: 96dpi) 800px,
      100vw`;
    }
  }

  private async openOverlay() {
    await this.overlay.openOverlay();
  }

  private renderInfographicImg() {
    return (
      <div
        onClick={() => {
          this.openOverlay();
        }}
        onKeyDown={(event) => {
          if (isEnterKey(event)) {
            this.openOverlay();
          }
        }}
        tabIndex={-1}
        hb-data-namespace={`${this.namespace}-open`}
        class='infographic-img-wrapper'
      >
        {this.responsiveImages != null
          ? renderResponsiveImages(
              this.responsiveImages,
              this.responsiveImagesSizes(),
              this.imageAltText,
              'infographic-images',
            )
          : ''}

        <button type='button' class='open-overlay-wrapper'>
          <span class='open-overlay-text'>
            {this.openOverlayText}
            <span class='open-overlay-icon' aria-hidden={true}>
              +
            </span>
          </span>
        </button>
        {this.mapHotSpots(this.hotSpots, this.renderHotSpots)}
      </div>
    );
  }

  private mapHotSpots(
    hotSpots: Array<HotSpot>,
    callBack: (hotSpot: HotSpot) => void,
  ) {
    return hotSpots.map((hotSpot: HotSpot) => callBack(hotSpot), callBack);
  }

  private renderHotSpots({ top, left, ranking }: HotSpot) {
    const topCss = `${top}%`;
    const letCss = `${left}%`;
    return (
      <span
        class={'hotspot'}
        style={{ position: 'absolute', top: topCss, left: letCss }}
      >
        {ranking}
      </span>
    );
  }

  private renderHotSpotDetails({ ranking, headline, text }: HotSpot) {
    return (
      <div
        class={'hotspot-details'}
        hb-data-namespace={`${this.namespace}-link`}
      >
        <div class={'hotspot-wrapper'}>
          <div class={'rank hotspot'}>{ranking}</div>
        </div>

        <div class={'details-content'}>
          <div class={'details-headline'} innerHTML={headline} />
          <div class={'details-text'} innerHTML={text} />
        </div>
      </div>
    );
  }

  private getCssClassMap(): CssClassMap {
    return {
      'infographic-container-inner': true,
      [this.layout]: true,
    };
  }

  connectedCallback() {
    if (this.images === undefined) {
      return;
    }

    this.hotSpotsWatcher(this.propHotSpots);
    this.imagesWatcher(this.images);
  }

  render() {
    return (
      <Host>
        <div class='infographic-container'>
          <div class={this.getCssClassMap()}>
            {this.renderInfographicImg()}
            <cms-overlay
              open={this.open}
              headline={this.overlayHeadline}
              imageAltText={this.imageAltText}
              responsiveImages={this.responsiveImages}
              contentSlot='content'
              imageSlot='image-hotspots'
              namespace={this.namespace}
            >
              <div slot='content'>
                <div class='details-wrapper'>
                  {this.mapHotSpots(
                    this.hotSpots,
                    this.renderHotSpotDetails.bind(this),
                  )}
                </div>
              </div>
              <div slot='image-hotspots'>
                {this.mapHotSpots(
                  this.hotSpots,
                  this.renderHotSpots.bind(this),
                )}
              </div>
            </cms-overlay>
          </div>
        </div>
      </Host>
    );
  }
}
